<template>
  <div class="record-detail-page overview-page" style="margin-top: 8px">
    <EntityOverviewLayout
      v-if="record"
      :panelTabs="overviewPanels"
      defaultPanel="files"
    >
      <template #top-left>
        <RecordDetails
          :record="record"
          :showMetadata="false"
          @updateRecord="updateRecordDetails"
          @deleted="onDeleted"
        />
      </template>

      <template #top-right>
        <DisplayImageGallery entityType="record" :entityId="record.node_id" />
      </template>

      <template #bottom-left>
        <NodeMdList
          :key="record.node_id"
          assType="record"
          :assId="record.node_id"
        />
      </template>

      <template #panel-files>
        <Files
          ref="filesComp"
          :files="files"
          :folders="folders"
          :base-path="filesBasePath"
          :loadingTree="isFilesTreeLoading"
          :experimentId="record.node_id"
          :assId="record.node_id"
          :assType="'record'"
          :isUploading="isUploading"
          :uploadPercent="uploadPercent"
          :uploadLabel="uploadLabel"
          @addFiles="addFiles"
          @removeFile="removeFile"
        />
      </template>

      <template #panel-comments>
        <Comments
          :comments="comments"
          :users="users"
          :files="files"
          :entityId="record.node_id"
          @addComment="addComment"
          @removeComment="removeComment"
          @updateComment="handleUpdateComment"
        />
      </template>
    </EntityOverviewLayout>

    <FileUploadSession
      :visible="uploadSession.visible"
      :fileType="uploadSession.fileType"
      :entityId="uploadSession.entityId"
      :files="uploadSession.files"
      :entraId="loggedInUser?.entra_id"
      :folderId="uploadSession.folderId"
      @complete="handleUploadComplete"
      @close="handleUploadClose"
      @error="handleUploadError"
    />
  </div>
</template>

<script>
import RecordDetails from "@/components/RecordDetails.vue";
import Files from "@/components/ExperimentFiles.vue";
import Comments from "@/components/ExperimentComments.vue";
import NodeMdList from "@/components/NodeMdList.vue";
import FileUploadSession from "@/components/FileUploadSession.vue";
import EntityOverviewLayout from '@/components/EntityOverviewLayout.vue';
import DisplayImageGallery from '@/components/DisplayImageGallery.vue';
import { useEntityDiscussion } from '@/composables/useEntityDiscussion';
import axios from "axios";
import { parseFilesTree } from "@/utils/filesTree";
import { API_ENDPOINTS } from "@/services/config";
import apiService from "@/services/apiService";
import { useToast } from "vue-toastification";
import { mapState } from "vuex";

export default {
  components: { RecordDetails, Files, Comments, NodeMdList, FileUploadSession, EntityOverviewLayout, DisplayImageGallery },
  data() {
    return {
      record: null,

      files: [],
      folders: [],
      isFilesTreeLoading: false,
      filesBasePath: '',
      comments: [],
      users: [],

      isUploading: false,
      uploadPercent: 0,
      uploadLabel: "",
      uploadSession: {
        visible: false,
        fileType: '',
        entityId: null,
        files: [],
        folderId: null,
      },
    };
  },
  setup() {
    const toast = useToast();
    const discussion = useEntityDiscussion();
    return { toast, discussion };
  },
  computed: {
    ...mapState(["loggedInUser"]),
    overviewPanels() {
      return [
        { id: 'files', label: 'Files' },
        { id: 'comments', label: 'Comments' },
      ];
    },
  },
  created() {
    this.fetchRecord();
    this.fetchComments();
    this.fetchUsers();
  },
  methods: {
    async fetchRecord() {
      const recordId = this.$route.params.id;
      try {
        const response = await axios.get(API_ENDPOINTS.record(recordId), {
          params: { entra_id: this.loggedInUser?.entra_id },
        });
        this.record = response.data;
        this.fetchFiles();
      } catch (error) {
        this.toast.error("Failed to fetch record");
        console.error("Failed to fetch record:", error);
      }
    },

    async fetchFiles() {
      if (!this.record?.node_id) return;
      this.isFilesTreeLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.filesTree, {
          params: {
            file_type: 'record',
            entity_id: this.record.node_id,
            entra_id: this.loggedInUser.entra_id,
          },
        });
        const { files, folders, basePath } = parseFilesTree(response.data);
        this.files = files;
        this.folders = folders;
        this.filesBasePath = basePath;
      } catch (error) {
        console.error('Error fetching files/folders:', error);
        await this.fetchFilesLegacy();
      } finally {
        this.isFilesTreeLoading = false;
      }
    },
    async fetchFilesLegacy() {
      if (!this.record?.node_id) return;
      try {
        const [filesRes, foldersRes] = await Promise.all([
          axios.get(API_ENDPOINTS.files, { params: { file_type: 'record', entity_id: this.record.node_id, entra_id: this.loggedInUser.entra_id } }),
          axios.get(API_ENDPOINTS.folders, { params: { associative_type: 'record', associative_id: this.record.node_id, entra_id: this.loggedInUser.entra_id } }),
        ]);
        this.files = filesRes.data || [];
        this.folders = (foldersRes.data || []).map((folder) => ({
          ...folder,
          id: folder?.id ?? `virtual:${folder?.internal_path || folder?.path || Math.random().toString(36).slice(2)}`,
          backend_id: folder?.id ?? null,
          internal_path: folder?.internal_path || folder?.path || "",
          parent_id: folder?.parent_id ?? null,
          virtual: Boolean(folder?.virtual),
          entity_type: folder?.entity_type || null,
          entity_id: folder?.entity_id ?? null,
        }));
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          this.toast.error('Failed to fetch files');
          console.error('Failed to fetch files (legacy):', error);
        }
      }
    },
    async fetchComments() {
      try {
        this.comments = await this.discussion.fetchComments({
          entityType: 'record',
          entityId: this.$route.params.id,
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error("Failed to fetch comments");
        console.error("Failed to fetch comments:", error);
      }
    },

    async fetchUsers() {
      try {
        this.users = await this.discussion.fetchUsers({
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error("Failed to fetch users");
        console.error("Failed to fetch users:", error);
      }
    },

    async addFiles({ files, id, folderId }) {
      // Use record.node_id for file API
      const entityId = this.record?.node_id || id;
      this.uploadSession = {
        visible: true,
        fileType: 'record',
        entityId: entityId,
        files: Array.from(files),
        folderId: folderId || null,
      };
    },

    async handleUploadComplete() {
      await this.fetchFiles();
      if (this.$refs.filesComp?.fetchFolders) {
        await this.$refs.filesComp.fetchFolders();
      }
      this.uploadSession.visible = false;
    },

    handleUploadClose() {
      this.uploadSession.visible = false;
      this.fetchFiles();
    },

    handleUploadError(error) {
      console.error('Upload session error:', error);
    },

    async removeFile(fileId) {
      try {
        console.log("Attempting to remove file with ID:", fileId);
        await apiService.deleteFile(
          fileId,
          this.loggedInUser?.id,
          this.loggedInUser?.entra_id
        );
        this.files = this.files.filter((f) => f.id !== fileId);
        this.toast.success("File removed successfully!");
      } catch (error) {
        this.toast.error("Failed to remove file");
        console.error("Failed to remove file:", error);
      }
    },

    async addComment(commentText, author_id, files) {
      try {
        await this.discussion.addComment({
          entityType: 'record',
          entityId: this.$route.params.id,
          text: commentText,
          authorId: author_id,
          files,
          entraId: this.loggedInUser.entra_id,
        });

        if (files && files.length > 0) {
          await this.fetchFiles();
        }

        await this.fetchComments();
        this.toast.success("Comment added successfully!");
      } catch (error) {
        this.toast.error("Failed to add comment");
        console.error("Failed to add comment:", error);
      }
    },

    async removeComment(commentId) {
      try {
        await this.discussion.removeComment({
          commentId,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        await this.fetchFiles();
        this.toast.success("Comment removed successfully!");
      } catch (error) {
        this.toast.error("Failed to remove comment");
        console.error("Failed to remove comment:", error);
      }
    },

    async handleUpdateComment(commentId, newText, entityId) {
      try {
        await this.discussion.updateComment({
          commentId,
          entityType: 'record',
          entityId: entityId ?? this.$route.params.id,
          text: newText,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        this.toast.success("Comment updated successfully!");
      } catch (error) {
        this.toast.error("Failed to update comment");
        console.error("Failed to update comment:", error);
      }
    },

    async updateRecordDetails(updatedRecord) {
      const recordId = this.$route.params.id;
      try {
        await axios.put(
          API_ENDPOINTS.record(recordId),
          { ...updatedRecord, entra_id: this.loggedInUser.entra_id },
          { headers: { "Content-Type": "application/json" } }
        );

        this.toast.success("Record updated successfully!");
        await this.fetchRecord();
      } catch (error) {
        this.toast.error("Failed to update record");
        console.error("Failed to update record:", error);
      }
    },

    onDeleted() {
      this.$router.push({ name: "RecordList" });
    },
  },
};
</script>

<style scoped lang="scss">
.overview-page {
  padding-bottom: 12px;
}
</style>